// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldStyle } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { searchInputStyles } from "@rpsc/Overrides/Widgets/Header/SearchInput";
import Search from "@rpsc/XDL/Icons/IconSearch";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, styledScrollbar, xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

const sharedTextStyle: TypographyPresentationProps = {
    size: 12,
    lineHeight: "17px",
    weight: fontConfig.weight.regular,
    css: css`
        margin: 0;
    `,
};

const sharedHeadStyle: TypographyPresentationProps = {
    variant: "h6",
    css: css`
        margin: 0;
    `,
};

searchInputStyles.input = {
    mergeCss: true,
    cssOverrides: {
        formField: css`
            &&&& button {
                width: 40.5px;
                height: 32.5px;
                top: 0.8px;
                right: 0.8px;
                border-radius: 0 23px 23px 0;
                background: ${color("primary")};
                color: ${color("common.white")};
                border: 0;
                overflow: hidden;
                &:hover {
                    background: ${color("common.ui8")};
                }
            }
            &&&& button {
                overflow: hidden;
            }
            &&&& ${IconWrapper}, svg {
                background: transparent;
                padding: 0;
                color: ${color("common.white")};
                position: static;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
        inputSelect: css`
            height: 34px;
        `,
    },

    iconProps: {
        src: Search,
        size: 14,
    },
    css: css`
        &&&,
        &&&:focus {
            border-radius: 23px;
            padding-inline: 13px;
        }
    `,
};

searchInputStyles.tippyPopup = {
    main: {
        css: css`
            &.tippy-box {
                &:has(.content-wrapper:empty) {
                    display: none;
                }
                border-radius: 2px;
                ${xScreen("lte", "md")} {
                    border: none;
                }
                > .tippy-content {
                    padding: 0;
                    max-height: none;
                    overflow: hidden;
                }
                ${xScreen("lte", "md")} {
                    height: 100%;
                }
            }
        `,
    },
};

searchInputStyles.autocompleteWrapper = {
    css: css`
        display: flex;
        flex-direction: row;
        ${({ width }: any) => `width: ${width}px;`}
        ${xScreen("lte", "md")} {
            flex-direction: column;
            width: 100%;
            max-height: ${({ headerHeight = "0px" }: any) => css`calc(100vh - ${headerHeight})`};
            overflow: auto;
        }
        ${xScreen("lte", "sm")} {
            overflow: auto;
            ${styledScrollbar({ width: 4 })}
        }
        ${xScreen("lte", 1260)} {
            width: auto;
        }
    `,
};
searchInputStyles.fullScreenInput = {
    css: css`
        display: flex;
        gap: 12px;
        flex-wrap: nowrap;
        flex: 1;
    `,
};

searchInputStyles.autocompleteColumnWrapper = {
    css: css`
        padding: 12px;
        width: auto;
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 16px;
        min-width: 370px;
        ${xScreen("lte", "lg")} {
            min-width: 317px;
        }
        ${xScreen("lte", "md")} {
            min-width: 370px;
        }
        &.product-side {
            min-width: 100%;
        }
        ${({ hasContent }: any) =>
            hasContent
                ? css`
                      border-right: 1px solid ${color("common.border")};
                  `
                : ""};
        // max-height: ${({ headerHeight = "0px" }: any) => css`calc(100vh - ${headerHeight})`};
        max-height: 512px;

        ${styledScrollbar({ width: 4 })}
        ${xScreen("lte", "md")} {
            max-height: 100%;
            height: 100%;
            width: 100%;
            min-width: unset;
            overflow: unset;
            padding: 25px 16px 16px;
        }
        ${xScreen("lte", "md")} {
            ${({ hasContent }: any) =>
                hasContent
                    ? css`
                          border-right: 0;
                          border-bottom: 1px solid ${color("common.border")};
                          padding: 16px 0 24px 0;
                          margin: 0 16px;
                          width: calc(100% - 32px); // replace padding with margin;
                      `
                    : ""};
        }
    `,
};

searchInputStyles.blockWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        &:empty {
            display: none;
        }
    `,
};

searchInputStyles.autocompleteCategoriesStyles = {
    headerText: sharedHeadStyle,
    categoryItemText: sharedTextStyle,
    focusedCategoryItemText: sharedTextStyle,
    link: {
        typographyProps: sharedTextStyle,
    },
};
searchInputStyles.autocompleteBrandsStyles = {
    headerText: sharedHeadStyle,
    brandItemText: sharedTextStyle,
    link: {
        typographyProps: sharedTextStyle,
    },
    focusedLink: {
        typographyProps: sharedTextStyle,
    },
    focusedBrandItemText: sharedTextStyle,
};

searchInputStyles.autocompleteContentStyles = {
    headerText: sharedHeadStyle,
    focusedLink: {
        typographyProps: sharedTextStyle,
    },
    link: {
        typographyProps: sharedTextStyle,
    },
};
searchInputStyles.focusedInputWrapper = {
    css: css`
        display: flex;
        gap: 12px;
        flex-wrap: nowrap;
        flex: 1;
        height: 100%;
        align-items: center;
        transition: all 0.5s linear;
        ${({ isFocused }: any) =>
            isFocused &&
            css`
                ${xScreen("lte", "md")} {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    background: ${color("common.white")};
                    ${FormFieldStyle} {
                        width: 100%;
                    }
                }
            `}

        ${xScreen("lte", 767)} {
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            width: 100%;
            background: white;
            padding: 9px 16px;
            gap: 16px;
        }
    `,
};
searchInputStyles.searchPopUpWrapper = {
    css: css`
        ${({ width }: any) => `width: ${width}px;`}
    `,
};
searchInputStyles.searchHistoryStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
};
